import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import RefreshIcon from '@mui/icons-material/RefreshRounded';

type ActivityRow = {
  id: number;
  endpoint: string;
  model_name: string;
  job_status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string | null;
  metadata: {
    r2_key?: string;
    request_id?: string;
    eta_seconds?: number;
    eta_updated_at?: string;
  } | null;
};

async function fetchActivity(id: number): Promise<ActivityRow> {
  const res = await fetch(`/api/v1/history/${id}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`status fetch failed: ${res.status}`);
  return (await res.json()) as ActivityRow;
}

async function fetchPresignedUrl(id: number): Promise<string> {
  const res = await fetch(`/api/v1/history/${id}/presigned-url`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!res.ok) throw new Error(`presign failed: ${res.status}`);
  const data = await res.json();
  return data.video_url as string;
}

// Smooth fake progress: takes the most-recent server ETA, interpolates locally
// each second, and asymptotes to 95% so the bar never claims "done" until the
// server actually says so. When a fresh ETA arrives we re-anchor.
function useFakeProgress(row: ActivityRow | null): { value: number; etaText: string | null } {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!row || row.job_status === 'completed' || row.job_status === 'failed') return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [row]);

  if (!row) return { value: 0, etaText: null };

  const etaSeconds = row.metadata?.eta_seconds;
  const updatedAt = row.metadata?.eta_updated_at;
  if (etaSeconds == null || !updatedAt) {
    // No upstream ETA yet — show indeterminate-feeling slow creep up to 30%.
    const creep = Math.min(30, tick * 0.5);
    return { value: creep, etaText: row.job_status === 'pending' ? 'Queued' : null };
  }
  const elapsedSinceAnchor = (Date.now() - new Date(updatedAt).getTime()) / 1000;
  const remaining = Math.max(0, etaSeconds - elapsedSinceAnchor);
  // Total perceived job length = elapsed + remaining; anchor=updatedAt was at
  // (total - eta_seconds) progress. We render `1 - remaining/total` capped at .95.
  const total = elapsedSinceAnchor + etaSeconds;
  const ratio = total > 0 ? 1 - remaining / total : 0;
  const value = Math.max(2, Math.min(95, ratio * 100));
  const etaText =
    remaining < 30
      ? 'Almost done'
      : remaining < 90
        ? 'Less than a minute'
        : `About ${Math.ceil(remaining / 60)} minutes left`;
  // tick reference so React keeps re-rendering each second:
  void tick;
  return { value, etaText };
}

export function JobResult({
  activityId,
  resultType,
  onReset,
  resetLabel,
}: {
  activityId: number;
  resultType: 'video' | 'image';
  onReset: () => void;
  resetLabel: string;
}) {
  const [row, setRow] = useState<ActivityRow | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { value: progressValue, etaText } = useFakeProgress(row);

  useEffect(() => {
    setRow(null);
    setResultUrl(null);
    setError(null);
    let cancelled = false;
    let timer: NodeJS.Timeout | null = null;

    const tick = async () => {
      try {
        const r = await fetchActivity(activityId);
        if (cancelled) return;
        setRow(r);
        if (r.job_status === 'completed' && r.metadata?.r2_key) {
          const url = await fetchPresignedUrl(activityId);
          if (!cancelled) setResultUrl(url);
          return;
        }
        if (r.job_status === 'failed') return;
        timer = setTimeout(tick, 3_000);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
        timer = setTimeout(tick, 5_000);
      }
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activityId]);

  if (!row) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <CircularProgress size={18} />
        <Typography variant="body2">Submitting…</Typography>
      </Stack>
    );
  }

  if (row.job_status === 'failed') {
    return (
      <Stack spacing={2}>
        <Alert severity="error">{row.error_message ?? 'Generation failed'}</Alert>
        <Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onReset}>
            {resetLabel}
          </Button>
        </Box>
      </Stack>
    );
  }

  if (row.job_status !== 'completed' || !resultUrl) {
    return (
      <Stack spacing={2}>
        <Box>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
              {row.job_status === 'processing' ? 'Generating your video' : 'Queued'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {etaText ?? '…'}
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={progressValue} sx={{ height: 8, borderRadius: 1 }} />
        </Box>
        {error && <Alert severity="warning">{error}</Alert>}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {resultType === 'video' ? (
        <Box
          component="video"
          src={resultUrl}
          controls
          sx={{
            width: '100%',
            maxHeight: 540,
            borderRadius: 2,
            bgcolor: 'black',
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      ) : (
        <Box
          component="img"
          src={resultUrl}
          alt="result"
          sx={{
            width: '100%',
            maxHeight: 540,
            objectFit: 'contain',
            borderRadius: 2,
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      )}
      <Stack direction="row" spacing={1.5}>
        <Button startIcon={<DownloadIcon />} variant="outlined" href={resultUrl} download>
          Download
        </Button>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={onReset}>
          {resetLabel}
        </Button>
      </Stack>
    </Stack>
  );
}
